import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import axios from "axios";
import { apiUrl } from "../config";

const adminAuthHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("idToken")}`,
});

// Logo + strip variants admins can upload. Order matters: this is the layout
// order on the page (logos row, then strip row).
const APPLE_ASSET_GROUPS = [
  {
    label: "Logo",
    description: "Shown in the top-left of the card header.",
    keys: ["logo.png", "logo@2x.png", "logo@3x.png"],
  },
  {
    label: "Strip",
    description: "Full-width band behind the primary fields.",
    keys: ["strip.png", "strip@2x.png", "strip@3x.png"],
  },
];

// Apple expects pass.json colors as "rgb(r, g, b)" strings, but the native
// <input type="color"> emits "#rrggbb". Convert both directions so the UI
// can always work in hex while the wire format is rgb().
const rgbToHex = (rgb) => {
  if (!rgb || typeof rgb !== "string") return "#ffffff";
  const m = rgb.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  if (!m) return rgb.startsWith("#") ? rgb : "#ffffff";
  const toHex = (n) => Number(n).toString(16).padStart(2, "0");
  return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`;
};

const DEFAULT_COLORS = {
  backgroundColor: "#ffffff",
  foregroundColor: "#000000",
  labelColor: "#000000",
};

const AssetSlot = ({ assetKey, spec, current, onUploaded, onRemoved }) => {
  const fileInputRef = useRef(null);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const pickFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "image/png") {
      toast({
        title: "Must be a PNG",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("assetKey", assetKey);
      formData.append("image", file);
      const res = await axios.post(
        `${apiUrl}/admin/walletDefaults/image`,
        formData,
        {
          headers: {
            ...adminAuthHeader(),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      onUploaded(assetKey, {
        url: res.data.url,
        updatedAt: res.data.updatedAt,
        width: res.data.width,
        height: res.data.height,
      });
      if (res.data.warning) {
        toast({
          title: `${assetKey} uploaded (with warning)`,
          description: res.data.warning,
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: `${assetKey} uploaded`,
          status: "success",
          duration: 2500,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: "Upload failed",
        description:
          err?.response?.data?.error ||
          err.message ||
          "Could not upload the image",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await axios.delete(
        `${apiUrl}/admin/walletDefaults/image/${assetKey}`,
        { headers: adminAuthHeader() }
      );
      onRemoved(assetKey);
      toast({
        title: `${assetKey} removed`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Remove failed",
        description:
          err?.response?.data?.error || err.message || "Could not remove image",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <VStack
      align="stretch"
      spacing={2}
      p={3}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      bg="white"
    >
      <HStack justify="space-between" align="start">
        <Text fontWeight="medium" fontSize="sm">
          {assetKey}
        </Text>
        <VStack spacing={0} align="flex-end">
          <Text fontSize="xs" color="gray.500">
            max {spec.width}×{spec.height}
          </Text>
          {current?.width && current?.height ? (
            <Text
              fontSize="xs"
              color={
                current.width < spec.width || current.height < spec.height
                  ? "orange.500"
                  : "green.600"
              }
            >
              actual {current.width}×{current.height}
            </Text>
          ) : null}
        </VStack>
      </HStack>
      <Box
        position="relative"
        bg="gray.50"
        borderRadius="md"
        border="1px dashed"
        borderColor="gray.300"
        minH="80px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        {current?.url ? (
          <>
            <Image
              src={current.url}
              alt={`${assetKey} preview`}
              maxH="120px"
              maxW="100%"
              objectFit="contain"
            />
            <IconButton
              aria-label={`Remove ${assetKey}`}
              icon={<CloseIcon />}
              size="xs"
              position="absolute"
              top="4px"
              right="4px"
              onClick={handleRemove}
              colorScheme="blackAlpha"
              isDisabled={busy}
            />
          </>
        ) : (
          <Text fontSize="xs" color="gray.500" textAlign="center" px={2}>
            Using bundled default
          </Text>
        )}
      </Box>
      <Input
        ref={fileInputRef}
        type="file"
        accept="image/png"
        display="none"
        onChange={handleFile}
      />
      <Button size="xs" onClick={pickFile} isLoading={busy} loadingText="Working">
        {current?.url ? "Replace" : "Upload PNG"}
      </Button>
    </VStack>
  );
};

const ColorField = ({ label, hex, onChange }) => (
  <HStack spacing={3} align="center">
    <Text fontSize="sm" minW="140px">
      {label}
    </Text>
    <Input
      type="color"
      value={hex}
      onChange={(e) => onChange(e.target.value)}
      width="60px"
      height="32px"
      p={0}
      borderColor="gray.300"
      cursor="pointer"
    />
    <Text fontSize="xs" fontFamily="mono" color="gray.600">
      {hex}
    </Text>
  </HStack>
);

const DigitalCardsAdmin = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [images, setImages] = useState({});
  const [specs, setSpecs] = useState({});
  const [colorsHex, setColorsHex] = useState(DEFAULT_COLORS);
  const [savedColorsHex, setSavedColorsHex] = useState(DEFAULT_COLORS);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/admin/walletDefaults`,
          { headers: adminAuthHeader() }
        );
        setImages(res.data.images || {});
        setSpecs(res.data.specs || {});
        const saved = {
          backgroundColor:
            rgbToHex(res.data.colors?.backgroundColor) ||
            DEFAULT_COLORS.backgroundColor,
          foregroundColor:
            rgbToHex(res.data.colors?.foregroundColor) ||
            DEFAULT_COLORS.foregroundColor,
          labelColor:
            rgbToHex(res.data.colors?.labelColor) || DEFAULT_COLORS.labelColor,
        };
        setColorsHex(saved);
        setSavedColorsHex(saved);
      } catch (err) {
        toast({
          title: "Could not load digital card defaults",
          description: err?.response?.data?.error || err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [toast]);

  const isDirty = useMemo(() => {
    return (
      colorsHex.backgroundColor !== savedColorsHex.backgroundColor ||
      colorsHex.foregroundColor !== savedColorsHex.foregroundColor ||
      colorsHex.labelColor !== savedColorsHex.labelColor
    );
  }, [colorsHex, savedColorsHex]);

  const handleSaveColors = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${apiUrl}/admin/walletDefaults/colors`,
        {
          backgroundColor: colorsHex.backgroundColor,
          foregroundColor: colorsHex.foregroundColor,
          labelColor: colorsHex.labelColor,
        },
        { headers: adminAuthHeader() }
      );
      setSavedColorsHex(colorsHex);
      toast({
        title: "Colors saved",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Save failed",
        description: err?.response?.data?.error || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetColors = () => {
    setColorsHex(savedColorsHex);
  };

  const handleAssetUploaded = (assetKey, value) => {
    setImages((prev) => ({ ...prev, [assetKey]: value }));
  };

  const handleAssetRemoved = (assetKey) => {
    setImages((prev) => ({ ...prev, [assetKey]: null }));
  };

  // Download a synthetic .pkpass so the admin can audit colors/logo/strip
  // changes on a real device. responseType: 'blob' is required because the
  // backend returns binary pkpass; the JSON error case below pulls the body
  // text out of the blob so we can surface a useful message.
  const handlePreviewPass = async () => {
    setPreviewing(true);
    try {
      const res = await axios.get(
        `${apiUrl}/admin/walletDefaults/previewPass`,
        {
          headers: adminAuthHeader(),
          responseType: "blob",
        }
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.apple.pkpass",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "coupal-wallet-preview.pkpass";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({
        title: "Preview pass downloaded",
        description:
          "Open the file on an iPhone or Mac to add it to Apple Wallet.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      let description =
        err?.message || "Could not generate Apple Wallet preview pass.";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          description = parsed.error || description;
        } catch {
          // leave description as-is
        }
      } else if (err?.response?.data?.error) {
        description = err.response.data.error;
      }
      toast({
        title: "Preview failed",
        description,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <HStack justify="center" py={10}>
        <Spinner />
        <Text>Loading…</Text>
      </HStack>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Flex
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={3}
          mb={1}
        >
          <Heading size="md">Apple Wallet</Heading>
          <Button
            size="sm"
            variant="secondary"
            onClick={handlePreviewPass}
            isLoading={previewing}
            loadingText="Generating"
          >
            Add preview to Wallet
          </Button>
        </Flex>
        <Text fontSize="sm" color="gray.600">
          Defaults applied to every Apple Wallet pass. Campaigns can override
          text fields independently; image and color overrides per campaign are
          not yet supported. The preview pass uses sample data so you can audit
          colors, logo, and strip on a device.
        </Text>
      </Box>

      <Box
        p={4}
        bg="gray.50"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
      >
        <Heading size="sm" mb={3}>
          Colors
        </Heading>
        <VStack align="stretch" spacing={3}>
          <ColorField
            label="Card background"
            hex={colorsHex.backgroundColor}
            onChange={(v) =>
              setColorsHex((prev) => ({ ...prev, backgroundColor: v }))
            }
          />
          <ColorField
            label="Card font (foreground)"
            hex={colorsHex.foregroundColor}
            onChange={(v) =>
              setColorsHex((prev) => ({ ...prev, foregroundColor: v }))
            }
          />
          <ColorField
            label="Label color"
            hex={colorsHex.labelColor}
            onChange={(v) =>
              setColorsHex((prev) => ({ ...prev, labelColor: v }))
            }
          />
        </VStack>
        <Flex justify="flex-end" mt={4} gap={2}>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleResetColors}
            isDisabled={!isDirty || saving}
          >
            Reset
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleSaveColors}
            isLoading={saving}
            isDisabled={!isDirty}
          >
            Save colors
          </Button>
        </Flex>
      </Box>

      {APPLE_ASSET_GROUPS.map((group) => (
        <Box key={group.label}>
          <Heading size="sm" mb={1}>
            {group.label}
          </Heading>
          <Text fontSize="xs" color="gray.600" mb={3}>
            {group.description} Upload all three densities for crisp rendering
            across devices. Smaller PNGs are accepted; iOS renders them at
            native size.
          </Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            {group.keys.map((key) => (
              <AssetSlot
                key={key}
                assetKey={key}
                spec={specs[key] || { width: "?", height: "?" }}
                current={images[key]}
                onUploaded={handleAssetUploaded}
                onRemoved={handleAssetRemoved}
              />
            ))}
          </SimpleGrid>
        </Box>
      ))}

      <Divider />
      <Text fontSize="xs" color="gray.500">
        Image uploads land in Cloud Storage at <code>wallet-defaults/</code>;
        colors live at <code>platformConfig/walletDefaults</code> in Firestore.
        Changes take effect on the next pass download.
      </Text>
    </VStack>
  );
};

export default DigitalCardsAdmin;
